import { useMemo, useState, useEffect } from 'react';
import { useAuth } from '@services/auth';
import {
	useAdminViewAs,
	getAdminCalendarIdentity,
	getAdminEffectiveCompanyOrGardenerIdNum,
	isAdminViewingAsOther
} from '../lib/adminViewAs';
import { getCalendarEvents, getEventsForDate, type CalendarEvent, EVENT_COLORS } from '@utils/calendarEvents';
import { getMyCompany, updateCompanySchedule, type CompanyNonWorkingDate, type MyCompanyResponse } from '@services/companies';
import { getStoredCompanyScopeMode, setStoredCompanyScopeMode } from '@services/companies';
import { getMyCompanyContexts } from '@services/companies';
import { getMyTimeOff, addTimeOff, type UserTimeOffEntry } from '@services/users';
import { isCompanyWorkingDay } from '@utils/companySchedule';
import { toLocalDateString } from '@utils/dateUtils';
import { isUserOnTimeOff } from '@utils/userTimeOff';
import type { UserResponse } from '@services/users';
import { getGardeners } from '@services/users';
import { getCompanyGardeners } from '@services/gardeners';
import type { GanttTask } from '../types/gantt';
import type { Project } from '@data/projects';
import { getProjectById } from '@data/projects';
import * as tasksAPI from '@services/tasks';
import type { TaskResponse } from '@services/tasks';
import TaskDetailModal from '../components/TaskDetailModal';
import CompanyWorkspaceStrip from '../shared/CompanyWorkspaceStrip';
import { useI18n } from '../i18n';

type ViewMode = 'month' | 'week' | 'day';

// Convert database task to GanttTask format (aligned with Tasks/Schedule pages)
function dbTaskToGanttTask(dbTask: TaskResponse, project: Project): GanttTask {
	return {
		id: `db-${dbTask.id}`,
		name: dbTask.title,
		description: dbTask.description || undefined,
		startDate: dbTask.start_date ? new Date(dbTask.start_date).toISOString() : new Date().toISOString(),
		endDate: dbTask.end_date ? new Date(dbTask.end_date).toISOString() : new Date().toISOString(),
		duration: dbTask.duration || 1,
		progress: dbTask.progress || 0,
		status: dbTask.status || 'not_started',
		dependencies: dbTask.dependencies || [],
		isCritical: dbTask.is_critical || false,
		gardenerIds: dbTask.gardener_ids.map(id => String(id)),
		machineIds: dbTask.machine_ids || [],
		projectId: project.id,
		notes: dbTask.notes || undefined,
		// Store gardener names from API response
		gardenerNames: dbTask.gardener_names || [],
		projectCompanyName: dbTask.project_company_name?.trim() || undefined,
		legalSeasonalBanCandidate: Boolean(dbTask.legal_seasonal_ban_candidate),
		legalSeasonalBanReason: dbTask.legal_seasonal_ban_reason || undefined
	} as GanttTask & { gardenerNames?: string[] };
}

export default function Calendar() {
	const { user } = useAuth();
	const { viewAsTarget } = useAdminViewAs();
	const { language } = useI18n();
	const copy = (en: string, de: string, hr: string) =>
		language === 'de' ? de : language === 'hr' ? hr : en;
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';
	const labelStatus = (status?: string) => {
		if (status === 'not_started') return copy('not started', 'nicht gestartet', 'nije započeto');
		if (status === 'in_progress') return copy('in progress', 'in Arbeit', 'u tijeku');
		if (status === 'completed') return copy('completed', 'abgeschlossen', 'završeno');
		if (status === 'blocked') return copy('blocked', 'blockiert', 'blokirano');
		return String(status || '').replace('_', ' ');
	};
	const [currentDate, setCurrentDate] = useState(new Date());
	const [viewMode, setViewMode] = useState<ViewMode>('month');
	const [selectedDate, setSelectedDate] = useState<Date | null>(null);
	const [events, setEvents] = useState<CalendarEvent[]>([]);
	const [isLoadingEvents, setIsLoadingEvents] = useState(true);
	const [activeTypes, setActiveTypes] = useState<CalendarEvent['type'][]>([
		'task'
	]);
	const [companyWorkingDays, setCompanyWorkingDays] = useState<number[] | null>(null);
	const [companyNonWorkingDates, setCompanyNonWorkingDates] = useState<CompanyNonWorkingDate[]>([]);
	const [timeOffEntries, setTimeOffEntries] = useState<UserTimeOffEntry[]>([]);
	const [showAddPtoForm, setShowAddPtoForm] = useState(false);
	const [ptoFormFrom, setPtoFormFrom] = useState('');
	const [ptoFormTo, setPtoFormTo] = useState('');
	const [ptoFormLabel, setPtoFormLabel] = useState('');
	const [calendarMessage, setCalendarMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
	const [calendarScope, setCalendarScope] = useState<'self' | 'my_projects' | 'company' | 'all_companies'>(() => {
		// Default to "Only me" for gardeners; companies keep company-wide default.
		if (user?.role === 'company') return getStoredCompanyScopeMode() === 'all_companies' ? 'all_companies' : 'company';
		if (user?.role === 'gardener') return 'self';
		return 'self';
	});
	const [gardeners, setGardeners] = useState<UserResponse[]>([]);
	const [selectedTaskForModal, setSelectedTaskForModal] = useState<{ task: GanttTask; project: Project } | null>(null);
	const [isLoadingTaskDetail, setIsLoadingTaskDetail] = useState(false);
	const [hasMultipleCompanies, setHasMultipleCompanies] = useState(false);
	const [companyCountryCode, setCompanyCountryCode] = useState<string | null>(null);

	const calendarActor = useMemo(() => {
		if (!user) return { userId: '', role: 'seeker' as const };
		return getAdminCalendarIdentity(user, viewAsTarget);
	}, [user, viewAsTarget]);
	const lensCoGardenerForCompanyMeta = useMemo(
		() => (user ? getAdminEffectiveCompanyOrGardenerIdNum(user, viewAsTarget) : null),
		[user, viewAsTarget]
	);
	const ptoReadOnly = Boolean(user && isAdminViewingAsOther(user, viewAsTarget));
	const companyWorkspaceUserId = useMemo(() => {
		if (!user) return '';
		if (user.role === 'admin' && lensCoGardenerForCompanyMeta != null) {
			return String(lensCoGardenerForCompanyMeta);
		}
		return user.id;
	}, [user, lensCoGardenerForCompanyMeta]);
	const hasCompanyScheduleContext = useMemo(
		() =>
			Boolean(
				user &&
					(user.role === 'company' ||
						user.role === 'gardener' ||
						(user.role === 'admin' && lensCoGardenerForCompanyMeta != null))
			),
		[user, lensCoGardenerForCompanyMeta]
	);
	const showCompanyClosedDayMutations = useMemo(
		() =>
			Boolean(user && (user.role === 'company' || (user.role === 'admin' && viewAsTarget?.role === 'company'))),
		[user, viewAsTarget]
	);

	useEffect(() => {
		if (!user) return;
		setIsLoadingEvents(true);
		const scope = calendarScope;
		if (scope === 'all_companies') {
			setStoredCompanyScopeMode('all_companies');
		} else if (scope === 'company') {
			setStoredCompanyScopeMode('active');
		}
		getCalendarEvents(calendarActor.role, calendarActor.userId, scope)
			.then(setEvents)
			.catch(err => {
				console.error('Failed to load calendar events:', err);
				setEvents([]);
			})
			.finally(() => setIsLoadingEvents(false));
	}, [calendarActor.role, calendarActor.userId, calendarScope, user?.activeCompanyId]);

	useEffect(() => {
		let cancelled = false;
		if (!user) {
			setHasMultipleCompanies(false);
			return;
		}
		const effectiveId =
			user.role === 'admin' && lensCoGardenerForCompanyMeta != null
				? String(lensCoGardenerForCompanyMeta)
				: user.id;
		if (!effectiveId || (user.role !== 'company' && user.role !== 'gardener' && !(user.role === 'admin' && lensCoGardenerForCompanyMeta != null))) {
			setHasMultipleCompanies(false);
			return;
		}
		getMyCompanyContexts(effectiveId)
			.then((res) => {
				if (!cancelled) setHasMultipleCompanies((res.companies?.length ?? 0) > 1);
			})
			.catch(() => {
				if (!cancelled) setHasMultipleCompanies(false);
			});
		return () => {
			cancelled = true;
		};
	}, [user?.id, user?.role, user?.activeCompanyId, lensCoGardenerForCompanyMeta]);

	useEffect(() => {
		if (!hasMultipleCompanies && calendarScope === 'all_companies') {
			setCalendarScope('company');
		}
	}, [hasMultipleCompanies, calendarScope]);

	// Load gardeners for task detail modal (same company scoping as Tasks/Schedule)
	useEffect(() => {
		if (!user?.id) return;
		const loadGardeners = async () => {
			try {
				let apiGardeners: UserResponse[];
				if (user.role === 'admin' && lensCoGardenerForCompanyMeta != null) {
					const companyGardeners = await getCompanyGardeners(lensCoGardenerForCompanyMeta);
					apiGardeners = companyGardeners.map((g) => ({
						id: g.id,
						name: g.display_name || g.name || g.email || '',
						email: g.email,
						role: g.role,
						company_name: g.company_name,
						created_at: '',
						updated_at: ''
					}));
				} else if (user.role === 'company' || user.role === 'gardener') {
					const userIdNum = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
					if (!user.id || isNaN(userIdNum)) {
						apiGardeners = [];
					} else {
						const companyGardeners = await getCompanyGardeners(userIdNum);
						apiGardeners = companyGardeners.map((g) => ({
							id: g.id,
							name: g.display_name || g.name || g.email || '',
							email: g.email,
							role: g.role,
							company_name: g.company_name,
							created_at: '',
							updated_at: ''
						}));
					}
				} else {
					apiGardeners = await getGardeners();
				}
				setGardeners(apiGardeners);
			} catch (err) {
				console.error('Failed to load gardeners for calendar:', err);
				setGardeners([]);
			}
		};
		loadGardeners();
	}, [user?.id, user?.role, user?.activeCompanyId, lensCoGardenerForCompanyMeta]);

	const openTaskFromEvent = async (event: CalendarEvent) => {
		if (event.type !== 'task' || !event.taskId || !event.projectId) return;
		setIsLoadingTaskDetail(true);
		try {
			const taskIdNum = parseInt(String(event.taskId), 10);
			if (isNaN(taskIdNum)) return;
			const task = await tasksAPI.getTaskById(taskIdNum);
			const project = await getProjectById(String(event.projectId));
			if (!project) return;
			const ganttTask = dbTaskToGanttTask(task, project);
			setSelectedTaskForModal({ task: ganttTask, project });
		} catch (err) {
			console.error('Failed to load task details for calendar event:', err);
		} finally {
			setIsLoadingTaskDetail(false);
		}
	};

	useEffect(() => {
		let cancelled = false;
		const loadMeta = async () => {
			if (!user?.id) return;

			// Company schedule (working days + closed dates)
			const companyScheduleUserId = companyWorkspaceUserId;
			if (
				user.role === 'company' ||
				user.role === 'gardener' ||
				(user.role === 'admin' && lensCoGardenerForCompanyMeta != null)
			) {
				try {
					const myCompany: MyCompanyResponse = await getMyCompany(
						companyScheduleUserId,
						user.activeCompanyId ?? undefined
					);
					if (!cancelled && myCompany?.company) {
						const wd = (myCompany.company as any).working_days;
						setCompanyWorkingDays(Array.isArray(wd) && wd.length > 0 ? wd : null);
						setCompanyCountryCode((myCompany.company as any).country_code || null);
						setCompanyNonWorkingDates(
							Array.isArray((myCompany.company as any).non_working_dates)
								? (myCompany.company as any).non_working_dates
								: []
						);
					}
				} catch {
					if (!cancelled) {
						setCompanyWorkingDays(null);
						setCompanyCountryCode(null);
						setCompanyNonWorkingDates([]);
					}
				}
			} else {
				setCompanyWorkingDays(null);
				setCompanyCountryCode(null);
				setCompanyNonWorkingDates([]);
			}

			// User time off (PTO)
			try {
				const list = await getMyTimeOff(calendarActor.userId);
				if (!cancelled) {
					setTimeOffEntries(Array.isArray(list) ? list : []);
				}
			} catch {
				if (!cancelled) setTimeOffEntries([]);
			}
		};

		loadMeta();
		return () => {
			cancelled = true;
		};
		}, [user?.id, user?.role, user?.activeCompanyId, calendarActor.userId, lensCoGardenerForCompanyMeta, companyWorkspaceUserId]);

	const monthStart = useMemo(() => {
		const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
		return date;
	}, [currentDate]);

	const monthEnd = useMemo(() => {
		const date = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
		return date;
	}, [currentDate]);

	const startDate = useMemo(() => {
		const start = new Date(monthStart);
		const day = start.getDay();
		const diff = start.getDate() - day + (day === 0 ? -6 : 1);
		return new Date(start.setDate(diff));
	}, [monthStart]);

	const calendarDays = useMemo(() => {
		const days: Date[] = [];
		const current = new Date(startDate);
		
		for (let i = 0; i < 42; i++) {
			days.push(new Date(current));
			current.setDate(current.getDate() + 1);
		}
		
		return days;
	}, [startDate]);

	const monthName = currentDate.toLocaleDateString(locale, { month: 'long', year: 'numeric' });

	const handlePrevPeriod = () => {
		if (viewMode === 'month') {
			setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
		} else if (viewMode === 'week') {
			setCurrentDate(new Date(currentDate.getTime() - 7 * 24 * 60 * 60 * 1000));
		} else {
			setCurrentDate(new Date(currentDate.getTime() - 24 * 60 * 60 * 1000));
		}
	};

	const handleNextPeriod = () => {
		if (viewMode === 'month') {
			setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
		} else if (viewMode === 'week') {
			setCurrentDate(new Date(currentDate.getTime() + 7 * 24 * 60 * 60 * 1000));
		} else {
			setCurrentDate(new Date(currentDate.getTime() + 24 * 60 * 60 * 1000));
		}
	};

	const handleToday = () => {
		setCurrentDate(new Date());
		setSelectedDate(null);
	};

	const isToday = (date: Date) => {
		const today = new Date();
		return date.toDateString() === today.toDateString();
	};

	const isCurrentMonth = (date: Date) => {
		return date.getMonth() === currentDate.getMonth();
	};

	const getEventTypeLabel = (type: CalendarEvent['type']) => {
		switch (type) {
			case 'task': return copy('Task', 'Aufgabe', 'Zadatak');
			case 'project_milestone': return 'Milestone';
			case 'site_visit': return 'Site Visit';
			case 'completion': return 'Completion';
			default: return type;
		}
	};

	const getEventTypeIcon = (type: CalendarEvent['type']) => {
		switch (type) {
			case 'task': return 'bi-check2-square';
			case 'project_milestone': return 'bi-flag';
			case 'site_visit': return 'bi-geo-alt';
			case 'completion': return 'bi-stars';
			default: return 'bi-circle';
		}
	};

	const toggleType = (type: CalendarEvent['type']) => {
		setActiveTypes(prev =>
			prev.includes(type)
				? prev.length === 1
					? prev // always keep at least one event type visible
					: prev.filter(t => t !== type)
				: [...prev, type]
		);
	};

	const filteredEvents = useMemo(() => {
		let list = events.filter(event => activeTypes.includes(event.type));
		// Only show task events on company working days (no dots on weekends/holidays)
		const workingDays = companyWorkingDays ?? [1, 2, 3, 4, 5];
		const nonWorking = companyNonWorkingDates ?? [];
		list = list.filter(event => {
			if (event.type !== 'task') return true;
			const d = event.date instanceof Date ? event.date : new Date(event.date);
			return isCompanyWorkingDay(d, workingDays, nonWorking);
		});
		return list;
	}, [events, activeTypes, companyWorkingDays, companyNonWorkingDates]);

	const selectedDateEvents = useMemo(() => {
		if (!selectedDate) return [];
		const eventsForDay = getEventsForDate(filteredEvents, selectedDate);
		return [...eventsForDay].sort((a, b) => a.date.getTime() - b.date.getTime());
	}, [filteredEvents, selectedDate]);

	const selectedDateMeta = useMemo(() => {
		if (!selectedDate) return null;
		const dateStr = toLocalDateString(selectedDate);
		const hasSchedule = hasCompanyScheduleContext && companyWorkingDays !== null;
		const isWorking = hasSchedule
			? isCompanyWorkingDay(selectedDate, companyWorkingDays, companyNonWorkingDates)
			: true;
		const isNonWorking = hasSchedule && !isWorking;
		const ptoForDay = timeOffEntries.filter(entry => entry.start_date <= dateStr && dateStr <= entry.end_date);
		const companyClosed = companyNonWorkingDates.find(d => d.date === dateStr) || null;
		return { isNonWorking, ptoForDay, companyClosed, dateStr };
	}, [selectedDate, user?.role, companyWorkingDays, companyNonWorkingDates, timeOffEntries]);

	const hasTimeOffOverlap = (startDate: string, endDate: string): boolean => {
		const rangeStart = startDate <= endDate ? startDate : endDate;
		const rangeEnd = startDate <= endDate ? endDate : startDate;
		return timeOffEntries.some(
			(entry) => entry.start_date <= rangeEnd && rangeStart <= entry.end_date
		);
	};
	const hasTaskOverlap = (startDate: string, endDate: string): boolean => {
		const rangeStart = startDate <= endDate ? startDate : endDate;
		const rangeEnd = startDate <= endDate ? endDate : startDate;
		const currentUserId = calendarActor.userId;
		return events.some((event) => {
			if (event.type !== 'task') return false;
			// Warn only for tasks assigned to the current user.
			// In "Only me" scope, backend already filters to the user's own tasks.
			const isOwnTask = event.assignedGardenerId
				? String(event.assignedGardenerId) === currentUserId
				: calendarScope === 'self';
			if (!isOwnTask) return false;
			const eventDate = toLocalDateString(event.date);
			return rangeStart <= eventDate && eventDate <= rangeEnd;
		});
	};
	const alreadyHasPtoForSelectedDay = Boolean(selectedDateMeta && selectedDateMeta.ptoForDay.length > 0);

	if (!user) {
		return null;
	}

	return (
		<div>
			{calendarMessage && (
				<div className={`alert alert-${calendarMessage.type === 'success' ? 'success' : 'danger'} alert-dismissible fade show py-2`} role="alert">
					{calendarMessage.text}
					<button type="button" className="btn-close btn-sm" onClick={() => setCalendarMessage(null)} aria-label="Close" />
				</div>
			)}
			<CompanyWorkspaceStrip />
			<div className="d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2">
				<h1 className="h3 mb-0">{copy('Calendar', 'Kalender', 'Kalendar')}</h1>
				{hasCompanyScheduleContext && !ptoReadOnly && (
					<button
						type="button"
						className="btn btn-outline-secondary btn-sm"
						onClick={() => {
							setShowAddPtoForm(prev => !prev);
							if (showAddPtoForm) {
								setPtoFormFrom('');
								setPtoFormTo('');
								setPtoFormLabel('');
							}
						}}
					>
						{showAddPtoForm ? copy('Cancel', 'Abbrechen', 'Odustani') : copy('Add time off', 'Abwesenheit hinzufügen', 'Dodaj odsutnost')}
					</button>
				)}
				<div className="d-flex gap-2 align-items-center">
					{hasCompanyScheduleContext && (
						<div className="btn-group me-1" role="group" aria-label="Calendar scope">
							<button
								type="button"
								className={`btn btn-sm ${calendarScope === 'self' ? 'btn-success' : 'btn-outline-secondary'}`}
								onClick={() => setCalendarScope('self')}
							>
								{copy('Only me', 'Nur ich', 'Samo ja')}
							</button>
							<button
								type="button"
								className={`btn btn-sm ${calendarScope === 'my_projects' ? 'btn-success' : 'btn-outline-secondary'}`}
								onClick={() => setCalendarScope('my_projects')}
							>
								{copy('My projects', 'Meine Projekte', 'Moji projekti')}
							</button>
							{hasCompanyScheduleContext && (
								<button
									type="button"
									className={`btn btn-sm ${calendarScope === 'company' ? 'btn-success' : 'btn-outline-secondary'}`}
									onClick={() => setCalendarScope('company')}
								>
									{copy('Active company', 'Aktives Unternehmen', 'Aktivna tvrtka')}
								</button>
							)}
							{hasCompanyScheduleContext && hasMultipleCompanies && (
								<button
									type="button"
									className={`btn btn-sm ${calendarScope === 'all_companies' ? 'btn-primary' : 'btn-outline-primary'}`}
									onClick={() => setCalendarScope('all_companies')}
								>
									{copy('All companies', 'Alle Unternehmen', 'Sve tvrtke')}
								</button>
							)}
						</div>
					)}
					<div className="btn-group" role="group">
						<button
							type="button"
							className={`btn btn-sm ${viewMode === 'month' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setViewMode('month')}
						>
							{copy('Month', 'Monat', 'Mjesec')}
						</button>
						<button
							type="button"
							className={`btn btn-sm ${viewMode === 'week' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setViewMode('week')}
						>
							{copy('Week', 'Woche', 'Tjedan')}
						</button>
						<button
							type="button"
							className={`btn btn-sm ${viewMode === 'day' ? 'btn-success' : 'btn-outline-secondary'}`}
							onClick={() => setViewMode('day')}
						>
							{copy('Day', 'Tag', 'Dan')}
						</button>
					</div>
					<button className="btn btn-sm btn-outline-secondary" onClick={handleToday}>
						{copy('Today', 'Heute', 'Danas')}
					</button>
				</div>
			</div>
			{hasCompanyScheduleContext && (
				<p className="text-muted small mb-3">
					{copy('Viewing:', 'Ansicht:', 'Prikaz:')}{' '}
					<strong>
						{calendarScope === 'all_companies'
							? copy('All companies', 'Alle Unternehmen', 'Sve tvrtke')
							: calendarScope === 'company'
								? user.companyName?.trim() || copy('Active company', 'Aktives Unternehmen', 'Aktivna tvrtka')
								: calendarScope === 'my_projects'
									? copy('My projects', 'Meine Projekte', 'Moji projekti')
									: copy('Only me', 'Nur ich', 'Samo ja')}
					</strong>
				</p>
			)}

			{showAddPtoForm && hasCompanyScheduleContext && !ptoReadOnly && (
				<div className="card mb-3">
					<div className="card-body py-2">
						<div className="row g-2 align-items-end">
							<div className="col-auto">
								<label className="form-label small mb-0">{copy('From', 'Von', 'Od')}</label>
								<input
									type="date"
									className="form-control form-control-sm"
									value={ptoFormFrom}
									onChange={e => setPtoFormFrom(e.target.value)}
								/>
							</div>
							<div className="col-auto">
								<label className="form-label small mb-0">{copy('To', 'Bis', 'Do')}</label>
								<input
									type="date"
									className="form-control form-control-sm"
									value={ptoFormTo}
									onChange={e => setPtoFormTo(e.target.value)}
								/>
							</div>
							<div className="col-auto">
								<label className="form-label small mb-0">{copy('Label (optional)', 'Bezeichnung (optional)', 'Oznaka (opcionalno)')}</label>
								<input
									type="text"
									className="form-control form-control-sm"
									placeholder={copy('e.g. Vacation', 'z. B. Urlaub', 'npr. Godišnji odmor')}
									value={ptoFormLabel}
									onChange={e => setPtoFormLabel(e.target.value)}
								/>
							</div>
							<div className="col-auto">
								<button
									type="button"
									className="btn btn-sm btn-success"
									onClick={async () => {
										if (ptoReadOnly) return;
										const from = ptoFormFrom.trim() || ptoFormTo.trim();
										const to = ptoFormTo.trim() || ptoFormFrom.trim();
										if (!from || !to) {
											setCalendarMessage({ type: 'error', text: copy('Please enter From and To dates.', 'Bitte Von- und Bis-Datum eingeben.', 'Unesite datume Od i Do.') });
											return;
										}
										if (hasTimeOffOverlap(from, to)) {
											setCalendarMessage({
												type: 'error',
												text: copy(
													'You already have time off for one or more selected dates.',
													'Sie haben bereits Abwesenheit fuer eines oder mehrere ausgewaehlte Daten.',
													'Vec imate odsutnost za jedan ili vise odabranih datuma.'
												)
											});
											return;
										}
										if (hasTaskOverlap(from, to)) {
											const proceed = window.confirm(
												copy(
													'You already have one or more tasks on the selected PTO dates. Add PTO anyway?',
													'Sie haben bereits eine oder mehrere Aufgaben an den ausgewaehlten PTO-Daten. PTO trotzdem hinzufuegen?',
													'Vec imate jedan ili vise zadataka na odabranim datumima odsutnosti. Svejedno dodati odsutnost?'
												)
											);
											if (!proceed) return;
										}
										try {
											const added = await addTimeOff(calendarActor.userId, {
												start_date: from,
												end_date: to,
												label: ptoFormLabel.trim() || null,
											});
											setTimeOffEntries(prev => [...prev, added]);
											setCalendarMessage({ type: 'success', text: copy('Time off added.', 'Abwesenheit hinzugefügt.', 'Odsutnost dodana.') });
											setShowAddPtoForm(false);
											setPtoFormFrom('');
											setPtoFormTo('');
											setPtoFormLabel('');
										} catch (e) {
											console.error('Failed to add time off', e);
											setCalendarMessage({ type: 'error', text: copy('Failed to add time off.', 'Abwesenheit konnte nicht hinzugefügt werden.', 'Dodavanje odsutnosti nije uspjelo.') });
										}
									}}
								>
									{copy('Add', 'Hinzufügen', 'Dodaj')}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{viewMode === 'month' && (
				<div className="card">
					<div className="card-header d-flex justify-content-between align-items-center py-2">
						<button
							type="button"
							className="btn btn-sm btn-link text-decoration-none"
							onClick={handlePrevPeriod}
						>
							<i className="bi bi-chevron-left"></i>
						</button>
						<h2 className="h6 mb-0">{monthName}</h2>
						<button
							type="button"
							className="btn btn-sm btn-link text-decoration-none"
							onClick={handleNextPeriod}
						>
							<i className="bi bi-chevron-right"></i>
						</button>
					</div>
					<div className="card-body p-0">
						<div className="px-3 pt-3 pb-2 border-bottom bg-light-subtle">
							<div className="d-flex flex-wrap gap-2 align-items-center">
								<span className="small text-muted me-1">{copy('Show:', 'Anzeigen:', 'Prikaži:')}</span>
								{(['task'] as CalendarEvent['type'][]).map(type => {
									const isActive = activeTypes.includes(type);
									return (
										<button
											key={type}
											type="button"
											className={`btn btn-xs btn-sm py-0 px-2 rounded-pill ${isActive ? 'btn-success' : 'btn-outline-secondary'}`}
											onClick={() => toggleType(type)}
										>
											<span className="d-inline-flex align-items-center gap-1">
												<i className={`bi ${getEventTypeIcon(type)} small`}></i>
												<span className="small">{getEventTypeLabel(type)}</span>
											</span>
										</button>
									);
								})}
							</div>
						</div>
						<div className="calendar-grid">
							{[
								copy('Monday', 'Montag', 'Ponedjeljak'),
								copy('Tuesday', 'Dienstag', 'Utorak'),
								copy('Wednesday', 'Mittwoch', 'Srijeda'),
								copy('Thursday', 'Donnerstag', 'Četvrtak'),
								copy('Friday', 'Freitag', 'Petak'),
								copy('Saturday', 'Samstag', 'Subota'),
								copy('Sunday', 'Sonntag', 'Nedjelja')
							].map(day => (
								<div key={day} className="calendar-day-header">
									{day.slice(0, 3)}
								</div>
							))}
							
							{calendarDays.map((date, idx) => {
								const dayEvents = getEventsForDate(filteredEvents, date);
								const isCurrent = isCurrentMonth(date);
								const isTodayDate = isToday(date);
								const isSelected = selectedDate && date.toDateString() === selectedDate.toDateString();
								const weekday = date.getDay();
								const isWeekend = weekday === 0 || weekday === 6;
								const dateStr = toLocalDateString(date);
								const hasSchedule = hasCompanyScheduleContext && companyWorkingDays !== null;
								const isWorkingDay = hasSchedule ? isCompanyWorkingDay(date, companyWorkingDays, companyNonWorkingDates) : true;
								const isNonWorkingDay = hasSchedule && !isWorkingDay;
								const isPtoDay = timeOffEntries.length > 0 && isUserOnTimeOff(date, timeOffEntries);
								const nonWorkingClass = isNonWorkingDay && !isTodayDate && !isSelected ? 'non-working-day' : '';
								const ptoClass = isPtoDay ? 'pto-day' : '';
								
								return (
									<div
										key={idx}
										className={`calendar-day ${!isCurrent ? 'other-month' : ''} ${isTodayDate ? 'today' : ''} ${isSelected ? 'selected' : ''} ${isWeekend ? 'weekend' : ''} ${nonWorkingClass} ${ptoClass}`}
										onClick={() => setSelectedDate(new Date(date))}
									>
										<div className="calendar-day-number">{date.getDate()}</div>
										<div className="calendar-day-events">
											{dayEvents.slice(0, 3).map(event => (
												<div
													key={event.id}
													className="calendar-event-dot"
													style={{ backgroundColor: event.color }}
													title={event.title}
												></div>
											))}
											{dayEvents.length > 3 && (
												<div className="calendar-event-more">+{dayEvents.length - 3}</div>
											)}
										</div>
									</div>
								);
							})}
						</div>
					</div>
				</div>
			)}

			{selectedDate && (
				<div className="card mt-3">
					<div className="card-header d-flex justify-content-between align-items-center py-2">
						<h3 className="h6 mb-0">
							{selectedDate.toLocaleDateString(locale, { 
								weekday: 'long', 
								year: 'numeric', 
								month: 'long', 
								day: 'numeric' 
							})}
						</h3>
						<button
							type="button"
							className="btn btn-sm btn-link text-decoration-none"
							onClick={() => setSelectedDate(null)}
						>
							<i className="bi bi-x-lg"></i>
						</button>
					</div>
					<div className="card-body">
						{selectedDateMeta && (
							<div className="mb-2 small">
								{selectedDateMeta.ptoForDay.length > 0 && (
									<div className="mb-1">
										{selectedDateMeta.ptoForDay.map(entry => (
											<div key={entry.id} className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-1">
												<span className="badge bg-info text-bg-info me-1">{copy('Your time off', 'Ihre Abwesenheit', 'Vaša odsutnost')}</span>
												<span className="text-muted">{entry.label || copy('Time off', 'Abwesenheit', 'Odsutnost')}</span>
											</div>
										))}
										<div className="text-muted small">
											{copy(
												'To remove PTO, open Settings > My time off.',
												'Zum Entfernen von PTO oeffnen Sie Einstellungen > Meine Abwesenheit.',
												'Za uklanjanje odsutnosti otvorite Postavke > Moj godisnji odmor.'
											)}
										</div>
									</div>
								)}
								{selectedDateMeta.isNonWorking && (
									<div className="mb-1">
										<span className="badge text-bg-warning me-1">{copy('Non-working day', 'Nicht-Arbeitstag', 'Neradni dan')}</span>
										<span className="text-muted">{copy('According to your company schedule.', 'Laut Ihrem Unternehmenskalender.', 'Prema rasporedu vaše tvrtke.')}</span>
									</div>
								)}
								{selectedDateMeta.companyClosed && (
									<div className="mb-1 d-flex align-items-center justify-content-between flex-wrap gap-2">
										<div>
											<span className="badge text-bg-warning me-1">{copy('Company closed', 'Unternehmen geschlossen', 'Tvrtka zatvorena')}</span>
											<span className="text-muted">
												{selectedDateMeta.companyClosed.label || selectedDateMeta.companyClosed.date}
											</span>
										</div>
										{showCompanyClosedDayMutations && (
											<button
												type="button"
												className="btn btn-sm btn-outline-danger"
												onClick={async () => {
													if (!selectedDate) return;
													const dateStr = toLocalDateString(selectedDate);
													const remaining = companyNonWorkingDates.filter(d => d.date !== dateStr);
													try {
														await updateCompanySchedule(companyWorkspaceUserId, {
															working_days: companyWorkingDays,
															non_working_dates: remaining.map(d => ({ date: d.date, label: d.label ?? null })),
														});
														setCompanyNonWorkingDates(remaining);
													} catch (e) {
														console.error('Failed to remove closed date from calendar', e);
													}
												}}
											>
												{copy('Remove', 'Entfernen', 'Ukloni')}
											</button>
										)}
									</div>
								)}
								{!selectedDateMeta.companyClosed && showCompanyClosedDayMutations && (
									<div className="mb-1">
										<button
											type="button"
											className="btn btn-sm btn-outline-warning"
											onClick={async () => {
												if (!selectedDate) return;
												const dateStr = toLocalDateString(selectedDate);
												if (companyNonWorkingDates.some(d => d.date === dateStr)) return;
												const updated = [...companyNonWorkingDates, { id: 0, date: dateStr, label: null }];
												try {
													const schedule = await updateCompanySchedule(companyWorkspaceUserId, {
														working_days: companyWorkingDays,
														non_working_dates: updated.map(d => ({ date: d.date, label: d.label ?? null })),
													});
													setCompanyNonWorkingDates(Array.isArray(schedule.non_working_dates) ? schedule.non_working_dates : updated);
												} catch (e) {
													console.error('Failed to add closed date from calendar', e);
												}
											}}
										>
											{copy('Mark this day as company closed', 'Diesen Tag als geschlossen markieren', 'Označi ovaj dan kao zatvoren')}
										</button>
									</div>
								)}
								{hasCompanyScheduleContext && !ptoReadOnly && (
									<div className="mb-1">
										<button
											type="button"
											className="btn btn-sm btn-outline-secondary"
											disabled={alreadyHasPtoForSelectedDay}
											onClick={async () => {
												if (ptoReadOnly) return;
												if (!selectedDate) return;
												const dateStr = toLocalDateString(selectedDate);
												if (hasTimeOffOverlap(dateStr, dateStr)) {
													setCalendarMessage({
														type: 'error',
														text: copy(
															'Time off already exists for this day.',
															'Fuer diesen Tag existiert bereits eine Abwesenheit.',
															'Odsutnost za ovaj dan vec postoji.'
														)
													});
													return;
												}
												if (hasTaskOverlap(dateStr, dateStr)) {
													const proceed = window.confirm(
														copy(
															'You already have task(s) on this day. Add PTO anyway?',
															'Sie haben bereits Aufgabe(n) an diesem Tag. PTO trotzdem hinzufuegen?',
															'Vec imate zadatak/zadatke na ovaj dan. Svejedno dodati odsutnost?'
														)
													);
													if (!proceed) return;
												}
												try {
													const added = await addTimeOff(calendarActor.userId, {
														start_date: dateStr,
														end_date: dateStr,
													});
													setTimeOffEntries(prev => [...prev, added]);
													setCalendarMessage({ type: 'success', text: copy('Time off added for this day.', 'Abwesenheit für diesen Tag hinzugefügt.', 'Odsutnost dodana za ovaj dan.') });
												} catch (e) {
													console.error('Failed to add PTO from calendar', e);
													setCalendarMessage({ type: 'error', text: copy('Failed to add time off.', 'Abwesenheit konnte nicht hinzugefügt werden.', 'Dodavanje odsutnosti nije uspjelo.') });
												}
											}}
										>
											{alreadyHasPtoForSelectedDay
												? copy('PTO already added for this day', 'Abwesenheit fuer diesen Tag bereits vorhanden', 'Odsutnost je vec dodana za ovaj dan')
												: copy('Add PTO for this day', 'Abwesenheit fuer diesen Tag hinzufuegen', 'Dodaj odsutnost za ovaj dan')}
										</button>
									</div>
								)}
							</div>
						)}
						{selectedDateEvents.length === 0 ? (
							<p className="text-muted mb-0">{copy('No events scheduled for this date.', 'Für dieses Datum sind keine Ereignisse geplant.', 'Nema događaja za ovaj datum.')}</p>
						) : (
							<div className="list-group">
								{selectedDateEvents.map(event => {
									const isTaskClickable = event.type === 'task' && event.taskId && event.projectId;
									return (
									<div
										key={event.id}
										className="list-group-item d-flex align-items-start"
										style={isTaskClickable ? { cursor: 'pointer' } : undefined}
										onClick={() => {
											if (isTaskClickable) {
												void openTaskFromEvent(event);
											}
										}}
									>
										<div
											className="me-3 mt-1"
											style={{
												width: '12px',
												height: '12px',
												borderRadius: '50%',
												backgroundColor: event.color,
												flexShrink: 0
											}}
										></div>
										<div className="flex-grow-1">
											<div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
												<i className={`bi ${getEventTypeIcon(event.type)} text-muted small`}></i>
												<span>{event.title}</span>
												{calendarScope === 'all_companies' && event.projectCompanyName ? (
													<span className="badge text-bg-secondary">{event.projectCompanyName}</span>
												) : null}
											</div>
											<div className="small text-muted">
												{getEventTypeLabel(event.type)}
												{event.projectName && ` · ${event.projectName}`}
											</div>
											{event.status && (
												<span className={`badge mt-1 ${event.status === 'completed' ? 'text-bg-success' : event.status === 'in_progress' ? 'text-bg-warning' : 'text-bg-secondary'}`}>
													{labelStatus(event.status)}
												</span>
											)}
										</div>
										<div className="text-muted small">
											{event.date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
										</div>
									</div>
								);})}
							</div>
						)}
					</div>
				</div>
			)}

			{!selectedDate && (
				<div className="card mt-3">
					<div className="card-header py-2">
						<h3 className="h6 mb-0">{copy('Upcoming Events', 'Bevorstehende Ereignisse', 'Nadolazeći događaji')}</h3>
					</div>
					<div className="card-body">
						{filteredEvents.filter(e => e.date >= new Date()).slice(0, 5).length === 0 ? (
							<p className="text-muted mb-0">{copy('No upcoming events.', 'Keine bevorstehenden Ereignisse.', 'Nema nadolazećih događaja.')}</p>
						) : (
							<div className="list-group">
								{filteredEvents.filter(e => e.date >= new Date()).slice(0, 5).map(event => {
									const isTaskClickable = event.type === 'task' && event.taskId && event.projectId;
									return (
									<div
										key={event.id}
										className="list-group-item d-flex align-items-start"
										style={isTaskClickable ? { cursor: 'pointer' } : undefined}
										onClick={() => {
											if (isTaskClickable) {
												void openTaskFromEvent(event);
											}
										}}
									>
										<div
											className="me-3 mt-1"
											style={{
												width: '12px',
												height: '12px',
												borderRadius: '50%',
												backgroundColor: event.color,
												flexShrink: 0
											}}
										></div>
										<div className="flex-grow-1">
											<div className="fw-semibold d-flex align-items-center gap-2 flex-wrap">
												<i className={`bi ${getEventTypeIcon(event.type)} text-muted small`}></i>
												<span>{event.title}</span>
												{calendarScope === 'all_companies' && event.projectCompanyName ? (
													<span className="badge text-bg-secondary">{event.projectCompanyName}</span>
												) : null}
											</div>
											<div className="small text-muted">
												{getEventTypeLabel(event.type)}
												{event.projectName && ` · ${event.projectName}`}
											</div>
										</div>
										<div className="text-muted small text-end">
											<div>{event.date.toLocaleDateString(locale, { month: 'short', day: 'numeric' })}</div>
											<div>{event.date.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}</div>
										</div>
									</div>
								);})}
							</div>
						)}
					</div>
				</div>
			)}

			<div className="card mt-3">
				<div className="card-header py-2">
					<h3 className="h6 mb-0">{copy('Legend', 'Legende', 'Legenda')}</h3>
				</div>
				<div className="card-body">
					<div className="row g-3">
						{Object.entries(EVENT_COLORS)
							.filter(([type]) => activeTypes.includes(type as CalendarEvent['type']))
							.map(([type, color]) => (
								<div key={type} className="col-6 col-md-3 d-flex align-items-center gap-2">
									<div
										style={{
											width: '16px',
											height: '16px',
											borderRadius: '50%',
											backgroundColor: color,
											flexShrink: 0
										}}
									></div>
									<span className="small">{getEventTypeLabel(type as CalendarEvent['type'])}</span>
								</div>
							))}
						{hasCompanyScheduleContext && (
							<>
								<div className="col-6 col-md-3 d-flex align-items-center gap-2">
									<div className="rounded" style={{ width: '16px', height: '16px', backgroundColor: '#fff7e6', flexShrink: 0 }}></div>
									<span className="small">{copy('Non-working / closed', 'Nicht-Arbeit / geschlossen', 'Neradno / zatvoreno')}</span>
								</div>
								<div className="col-6 col-md-3 d-flex align-items-center gap-2">
									<div className="rounded" style={{ width: '16px', height: '16px', backgroundColor: '#bff3df', flexShrink: 0 }}></div>
									<span className="small">PTO</span>
								</div>
							</>
						)}
					</div>
				</div>
			</div>
			{selectedTaskForModal && (
				<TaskDetailModal
					task={selectedTaskForModal.task}
					project={selectedTaskForModal.project}
					companyCountryCode={companyCountryCode}
					gardeners={gardeners}
					onClose={() => setSelectedTaskForModal(null)}
					onUpdate={async () => {
						// Refresh events after task update
						setIsLoadingEvents(true);
						try {
							const fresh = await getCalendarEvents(user.role, user.id, calendarScope);
							setEvents(fresh);
						} catch (err) {
							console.error('Failed to refresh calendar events after task update:', err);
						} finally {
							setIsLoadingEvents(false);
						}
					}}
				/>
			)}
		</div>
	);
}

